import { COLORS_LIST } from "MyDashboards/components/Render/components/Chart/ChartStyles";
import { t } from "i18next";
import { Chart, ChartProps, valuesTable } from "../types";
const ChartTypes: Record<Chart, string> = {
  pie: "pie",
  column: "column",
  bar: "bar",
  donut: "pie",
  column_line: "",
  line: "spline",
};

const COLORS = [
  {
    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
    stops: [
      [0, "rgba(93,98,104,0.8)"],
      [1, "rgba(93,98,104, 1)"],
    ],
  },
  {
    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
    stops: [
      [0, "rgba(59, 187,185, 0.8)"],
      [1, "rgba(59, 187,185, 1)"],
    ],
  },

  "rgba(59, 187,185, 0.8)",
  "rgb(93,98,104)",
  "rgba(59, 187,185, 1)",
];

const getSeries = (
  series: valuesTable,
  type: Chart,
  categories: string[],
  colors?: any[]
) => {
  if (series.length) {
    const seriesToShow = series.filter((serie) => {
      const seriesWithValues = serie.values.filter((v) => !!v.value);
      return seriesWithValues.length > 0;
    });
    if (seriesToShow.length < 1)
      return [
        {
          name: t("configtypes.No data"),
          data: [],
        },
      ];

    if (type === "pie" || type === "donut") {
      // pie charts require y values
      return {
        type: "pie",
        name: t("configtypes." + categories[0]),
        data: seriesToShow?.map((ser, idx) => ({
          name: t("configtypes." + ser.name),
          visible: true,
          y: Number(ser.values[0]?.value) || null,
          formattedPoint: ser.values[0]?.valuewithoutsymbol || null,
          color: colors
            ? colors[idx % (colors.length - 1)]
            : COLORS[idx % (COLORS.length - 1)],
        })),
      };
    }
    if (type === "line") {
      return seriesToShow.map((ser, idx) => ({
        color: COLORS_LIST[idx % (COLORS_LIST.length - 1)],
        marker: { lineColor: COLORS_LIST[idx % (COLORS_LIST.length - 1)] },
        name: t("configtypes." + ser.name),
        visible: true,
        type: "spline",
        data: ser.values?.map(
          (v) =>
            ({ y: Number(v.value), formattedPoint: v.valuewithoutsymbol } ||
            null)
        ),
      }));
    }

    return seriesToShow.map((ser, idx) => {
      const serType =
        type === "column_line" ? (idx === 0 ? "column" : "spline") : type;
      return {
        // column line type requires the first series to be column and second to be line
        type: serType,
        name: t("configtypes." + ser.name),
        color:
          serType === "spline"
            ? COLORS_LIST[idx % (COLORS_LIST.length - 1)]
            : colors
            ? colors[idx % (colors.length - 1)]
            : COLORS[idx % (COLORS.length - 1)],
        plotOptions: { series: { connectNulls: false } },
        marker: {
          lineColor:
            serType === "spline"
              ? COLORS_LIST[idx % (COLORS_LIST.length - 1)]
              : colors
              ? colors[idx % (colors.length - 1)]
              : COLORS[idx % (COLORS.length - 1)],
        },
        visible: true,

        data: ser.values?.map((v) => ({
          y: Number(v.value) || null,
          formattedPoint: v.valuewithoutsymbol,
        })),
      };
    });
    // }
  }
  return [{ name: "No Data", data: [] }];
};
const getOptions = (
  props: ChartProps,
  disableAnimation: boolean = true,
  colors?: any[]
) => {
  const { categories, width, height, series, rtl, type, styles } = props;
  const options = {
    title: {
      text: type === "pie" || type === "donut" ? categories[0] : "", // title set to ''
      useHTML: true,
    },
    chart: {
      width,
      height,
      type: ChartTypes[type],
      animation: !disableAnimation,
    },
    xAxis: {
      categories: categories,
      labels: { style: { fontSize: "9px" } },
      reversed: rtl && type !== "bar",
      opposite: rtl && type === "bar",
    },
    yAxis: {
      min: 0,
      title: {
        text: t(series[0]?.values[0]?.unit),
      },
      labels: { style: { fontSize: "9px" } },
      opposite: rtl && type !== "bar",
      reversed: rtl && type === "bar",
    },
    legend: {
      useHTML: true,
      rtl: rtl,
      itemStyle: {
        textOverflow: undefined,
        fontSize: "9px",
      },
    },
    tooltip: { useHTML: true },
    direction: rtl ? "rtl" : "ltr",
    updateArgs: [true, true, true],
    allowChartUpdate: true,
    credits: {
      enabled: false,
    },
    plotOptions: {
      series: { animation: !disableAnimation },
      spline: styles?.spline,
      areaspline: styles?.areaspline,
      bar: styles?.bar,
      column: styles?.column,
      pie: type === "pie" ? styles?.pie : type === "donut" ? styles?.donut : "",
    },
    series: getSeries(series, type, categories, colors),
  };
  return options;
};

export default getOptions;
